import type {
	QuizStreamingChunk,
	GistPopulated,
	NotePopulated,
	Quiz,
	QuizInfo,
	QuizPopulated,
	Share,
	ApiResponse,
	UniqueId,
} from "@vita-learning/types";

import { makeUrl, revalidate } from "~/shared/utils";

import { resHttpClient, aiHttpClient, type HttpConfig } from "../../config";
import { assertCorrectUrlEndpoint } from "../../utils";

import { decodeQuiz } from "./helpers";

export const createQuiz = async (
	quiz: QuizInfo | Quiz
): Promise<QuizPopulated> => {
	const resp = await resHttpClient.post<ApiResponse<QuizPopulated>>(
		"/quizzes",
		quiz
	);

	if (!resp.success) throw resp.error;

	await revalidate("quizzes");

	return decodeQuiz(resp.data);
};

export const createManyQuizzes = async (
	quizzes: (QuizInfo | Quiz)[]
): Promise<QuizPopulated[]> => {
	const resp = await resHttpClient.post<ApiResponse<QuizPopulated[]>>(
		"/quizzes?bulk=true",
		quizzes
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("quizzes");

	return resp.data.map(decodeQuiz);
};

export const createQuizShareUrl = async (
	payload: UniqueId
): Promise<{ url: string }> => {
	const resp = await resHttpClient.post<ApiResponse<{ url: string }>>(
		"/quizzes/share-urls",
		payload
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return resp.data;
};

export const getQuiz = async (
	id: string,
	config?: HttpConfig
): Promise<QuizPopulated> => {
	const resp = await resHttpClient.get<ApiResponse<QuizPopulated>>(
		`/quizzes/${id}`,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return decodeQuiz(resp.data);
};

export const fetchQuizzes = async (
	url: string,
	config?: HttpConfig
): Promise<QuizPopulated[]> => {
	assertCorrectUrlEndpoint(url, "/quizzes");

	const resp = await resHttpClient.get<ApiResponse<QuizPopulated[] | null>>(
		url,
		config
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	return (resp.data || []).map(decodeQuiz);
};

export const updateQuiz = async (quiz: QuizInfo): Promise<QuizPopulated> => {
	if (!quiz.id) throw new Error("Quiz ID is required");

	const resp = await resHttpClient.put<ApiResponse<QuizPopulated>>(
		`/quizzes/${quiz.id}`,
		quiz
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("quizzes");

	return decodeQuiz(resp.data);
};

export const shareQuiz = async (
	id: string,
	share: Share
): Promise<QuizPopulated> => {
	const resp = await resHttpClient.patch<ApiResponse<QuizPopulated>>(
		`/quizzes/${id}/share`,
		{ ...share, id }
	);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("quizzes");

	return decodeQuiz(resp.data);
};

export const deleteQuiz = async (id: string): Promise<void> => {
	const resp = await resHttpClient.delete<ApiResponse<void>>(`/quizzes/${id}`);

	if (resp.debug) console[resp.debug.level](resp.debug);

	if (!resp.success) throw resp.error;

	await revalidate("quizzes");

	return resp.data;
};

export const generateQuiz = (
	quiz: Partial<Quiz> &
		(
			| {
					file?: {
						id: string;
						userId: string;
						title: string;
						ext: string;
						url: string;
						expiresAt: string;
					};
			  }
			| {
					gist?: GistPopulated;
			  }
			| {
					note?: NotePopulated;
			  }
			| {
					url?: string;
			  }
		),
	source: QuizInfo["source"],
	onStream: (chunk: QuizStreamingChunk) => void
) => {
	const url = makeUrl("/quizzes");

	url.searchParams.set("source", source);

	return aiHttpClient.post<void>(url.pathname + url.search, quiz, {
		headers: {
			// Accept server-sent events
			Accept: "text/event-stream",
			// Disabling compression for streaming responses
			"Accept-Encoding": "identity",
			// Send the data as JSON
			"Content-Type": "application/json",
			// Preventing caching
			"Cache-Control": "no-cache",
		},
		cache: "no-cache",
		onStream: chunk => {
			// Chunks might accumulate so split them by the new line
			const lines = chunk.split("\n").filter(line => line.trim() !== "");
			const DATA_CHUNK_PREFIX = "data: ";
			const ERROR_CHUNK_PREFIX = "error: ";

			lines.forEach(line => {
				try {
					if (line.startsWith(DATA_CHUNK_PREFIX))
						onStream(
							JSON.parse(
								line.replace(DATA_CHUNK_PREFIX, "").trim()
							) as QuizStreamingChunk
						);

					if (line.startsWith(ERROR_CHUNK_PREFIX))
						throw JSON.parse(line.replace(ERROR_CHUNK_PREFIX, "").trim());
				} catch (e) {
					if (!(e instanceof SyntaxError)) throw e;

					console.error(e);
				}
			});
		},
	});
};
